import {
  ArrowBack,
  Receipt,
  Check,
  Warning,
  Info,
  LocalShipping,
} from '@mui/icons-material';
import React, { useEffect } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { FormControl, TextField } from '@mui/material';

import Loading from '../../components/global/loading/Loading';
import PriceList from '../../components/global/order/PriceList';
import Confirm from '../../components/modals/confirmModal/Confirm';
import CancelOrder from '../../components/modals/order/cancelOrder/CancelOrder';
import { useOrder } from '../../context/OrderContext';
import PaymentForm from '../../forms/order/PaymentForm';
import translateDeliveryStatus from '../../helperFunctions/translateDeliveryStatus';
import translateOrderStatus from '../../helperFunctions/translateOrderStatus';
import translatePaymentStatus from '../../helperFunctions/translatePaymentStatus';
import {
  DeliveryMethod,
  DeliveryStatus,
  Order as OrderModel,
  OrderStatus,
  PaymentStatus,
} from '../../models/Order';
import OrderConfirmation from './OrderConfirmation';
import translateDeliveryMethod from '../../helperFunctions/translateDeliveryMethod';
import getIconForDeliveryMethod from '../../helperFunctions/getIconForDeliveryMethod';
import { useAuth } from '../../context/AuthContext';
import ReturnOrderForm from '../../forms/order/ReturnOrderForm';
import { useReturn } from '../../context/ReturnContext';
import { useConfig } from '../../utils/useConfig';
import Seo from '../../components/seo/Seo';

interface IFormInput {
  packageNumber: string;
}

function Order() {
  const config = useConfig();
  const { isAuthenticated } = useAuth();
  const { id } = useParams();
  const {
    getOrder,
    changeOrderStatus,
    setPackageNumber,
    packageSent,
    finishOrder,
  } = useOrder();
  const [order, setOrder] = React.useState<OrderModel | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isOnAdmin = location.pathname.includes('admin');

  const { createReturn } = useReturn();

  const [showPackageSentModal, setShowPackageSentModal] = React.useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = React.useState(false);
  const [showConfirmChangeStatusModal, setShowConfirmChangeStatusModal] =
    React.useState(false);
  const [showFinishOrderModal, setShowFinishOrderModal] = React.useState(false);

  const newStatus =
    order?.orderStatus === OrderStatus.PENDING
      ? OrderStatus.IN_PROGRESS
      : order?.orderStatus === OrderStatus.IN_PROGRESS
        ? OrderStatus.COMPLETED
        : null;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormInput>();

  useEffect(() => {
    if (
      isAuthenticated &&
      location.pathname.startsWith(encodeURI('/zamówienie'))
    ) {
      const isPaymentPage = location.pathname.includes(encodeURI('płatność'));
      navigate(`/profil/zamówienia/${id}${isPaymentPage ? '/płatność' : ''}`);
    }
  }, [id, isAuthenticated, location.pathname, navigate]);

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const fetchOrder = React.useCallback(async () => {
    try {
      const order = await getOrder(id as string);
      setOrder(order);
    } catch (error: any) {
      toast.error(error.message);
      navigate('/');
    }
  }, [getOrder, id, navigate]);

  const setPackageSent = async () => {
    const toastId = toast.loading('Potwierdzanie wysłania przesyłki...');
    try {
      await packageSent(id as string);
      toast.update(toastId, {
        render: 'Przesyłka została wysłana',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      fetchOrder();
    } catch (error: Error | any) {
      toast.update(toastId, {
        render:
          error.message ||
          'Wystąpił błąd podczas potwierdzania wysłania przesyłki',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleFinishOrder = async () => {
    const toastId = toast.loading('Zamykam zamówienie...');
    try {
      await finishOrder(id as string);
      toast.update(toastId, {
        render: 'Zamówienie zostało zamknięte',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      fetchOrder();
    } catch (error: Error | any) {
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas zamykania zamówienia',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const updateOrderStatus = async () => {
    if (!newStatus) {
      return;
    }
    const toastId = toast.loading('Zmienianie statusu zamówienia...');
    try {
      await changeOrderStatus(id as string);
      toast.update(toastId, {
        render: 'Status zamówienia został zmieniony',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      fetchOrder();
    } catch (error: Error | any) {
      toast.update(toastId, {
        render:
          error.message || 'Wystąpił błąd podczas zmiany statusu zamówienia',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    const toastId = toast.loading('Wysyłanie numeru listu przewozowego...');
    try {
      await setPackageNumber(id as string, data.packageNumber);
      toast.update(toastId, {
        render: 'Numer listu przewozowego został wysłany',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      fetchOrder();
    } catch (error: Error | any) {
      toast.update(toastId, {
        render:
          error.message ||
          'Wystąpił błąd podczas wysyłania numeru listu przewozowego',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleReturnSubmit = async (data: any) => {
    try {
      const { returnId } = await createReturn({
        orderId: id as string,
        comments: data.additional,
        items: data.items,
      });
      toast.success('Formularz zwrotu został wysłany');
      // Przekierowanie do strony zwrotu
      navigate(`/zwrot/${returnId}`);
    } catch (error: any) {
      toast.error(
        error.message || 'Wystąpił błąd podczas składania formularza zwrotu',
      );
    }
  };

  useEffect(() => {
    if (!id) {
      toast.error('Nie znaleziono zamówienia');
      navigate('/');
    }

    fetchOrder();
  }, [id, getOrder, navigate, fetchOrder]);

  const handleBackClick = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 1) {
      const newPath = '/' + pathParts.slice(0, -1).join('/');
      navigate(newPath);
    } else {
      navigate('/');
    }
  };

  // Kolorystyka statusów zamówienia
  const getOrderStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-200';
      case OrderStatus.IN_PROGRESS:
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case OrderStatus.CANCELLED:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  // Kolorystyka statusów płatności
  const getPaymentStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.COMPLETED:
        return 'text-green-600';
      case PaymentStatus.FAILED:
        return 'text-red-600';
      default:
        return 'text-amber-600';
    }
  };

  // Tutaj dodaję nowy helper do sprawdzania czy zamówienie ma adres
  const hasFullAddress = (order: OrderModel) => {
    return (
      order.street && order.buildingNumber && order.city && order.postalCode
    );
  };

  // Tutaj dodaję nowy helper do sprawdzania czy zamówienie ma adres wybrany przez ID
  const hasAddressId = (order: OrderModel) => {
    return !!order.addressId;
  };

  if (!order) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <motion.div
        className="mx-auto px-4 py-8 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Przycisk powrotu */}
        {(location.pathname.includes('admin') ||
          location.pathname.includes('profil')) && (
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowBack fontSize="small" />
            <span className="font-medium">Powrót</span>
          </motion.button>
        )}

        {/* Panel administracyjny */}
        {isOnAdmin && (
          <motion.div
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8"
            variants={itemVariants}
          >
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h2 className="text-lg font-medium text-gray-800">
                Panel administracyjny
              </h2>
            </div>

            <div className="p-5 space-y-6">
              <div>
                <h3 className="font-medium text-gray-800 mb-2">
                  Akcje administracyjne
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  Poniżej znajdziesz przyciski do zarządzania zamówieniem oraz
                  informacje krok po kroku, które pomogą Ci w obsłudze
                  zamówienia.
                </p>

                <div className="flex flex-wrap gap-3 mb-4">
                  {order.orderStatus === OrderStatus.PENDING && (
                    <div>
                      {order.paymentStatus === PaymentStatus.COMPLETED && (
                        <p className="text-red-500 text-sm mb-2">
                          Zamówienie zostało opłacone. Anulowanie rozpocznie
                          procedurę zwrotu.
                        </p>
                      )}
                      <motion.button
                        onClick={() => setShowCancelOrderModal(true)}
                        className="px-4 py-2 bg-red-50 border border-red-300 text-red-700 hover:bg-red-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Warning fontSize="small" />
                        Anuluj zamówienie
                      </motion.button>
                    </div>
                  )}

                  {order.paymentStatus === PaymentStatus.COMPLETED &&
                    order.orderStatus === OrderStatus.PENDING &&
                    newStatus && (
                      <motion.button
                        onClick={() => setShowConfirmChangeStatusModal(true)}
                        className="px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Check fontSize="small" />
                        Zaznacz jako &quot;{translateOrderStatus(newStatus)}
                        &quot;
                      </motion.button>
                    )}
                </div>

                {order.deliveryStatus === DeliveryStatus.PENDING &&
                  order.orderStatus === OrderStatus.IN_PROGRESS && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                      <p className="text-gray-600 text-sm mb-3">
                        Kliknij, aby wygenerować list przewozowy na stronie
                        InPost. Następnie, wpisz numer listu przewozowego w polu
                        poniżej i kliknij przycisk 'Zatwierdź'.
                      </p>

                      <motion.button
                        onClick={() =>
                          window.open(
                            'https://manager.paczkomaty.pl/shipments/send/simple',
                            '_blank',
                          )
                        }
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors flex items-center gap-1 mb-4"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span>Utwórz list przewozowy</span>
                        <LocalShipping sx={{ fontSize: 18 }} />
                      </motion.button>

                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-4"
                      >
                        <h4 className="font-medium text-gray-800">
                          Wprowadź numer listu przewozowego
                        </h4>

                        <FormControl fullWidth>
                          <Controller
                            name="packageNumber"
                            control={control}
                            defaultValue=""
                            rules={{
                              required:
                                'Numer listu przewozowego jest wymagany',
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                label="Numer listu przewozowego"
                                error={!!errors.packageNumber}
                                helperText={errors.packageNumber?.message}
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>

                        <motion.button
                          type="submit"
                          className="px-4 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Zatwierdź
                        </motion.button>
                      </form>
                    </div>
                  )}

                {order.deliveryStatus === DeliveryStatus.READY &&
                  order.orderStatus === OrderStatus.IN_PROGRESS && (
                    <motion.button
                      onClick={() => setShowPackageSentModal(true)}
                      className="px-4 py-2 bg-green-50 border border-green-300 text-green-700 hover:bg-green-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>Przesyłka wysłana</span>
                      <LocalShipping sx={{ fontSize: 18 }} />
                    </motion.button>
                  )}

                {order.orderStatus === OrderStatus.IN_PROGRESS &&
                  order.deliveryStatus === DeliveryStatus.SHIPPED && (
                    <motion.button
                      onClick={() => setShowFinishOrderModal(true)}
                      className="px-4 py-2 bg-green-50 border border-green-300 text-green-700 hover:bg-green-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>Zamknij zamówienie</span>
                      <Check sx={{ fontSize: 18 }} />
                    </motion.button>
                  )}
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">
                  Profil klienta
                </h3>
                {order.userId ? (
                  <Link
                    to={`/admin/użytkownicy/${order.userId}`}
                    className="text-primary hover:text-primary-dark underline transition-colors"
                  >
                    Zobacz profil klienta
                  </Link>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Klient nie jest zarejestrowany
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Nagłówek zamówienia */}
        <motion.div className="mb-8" variants={itemVariants}>
          <h1 className="text-2xl font-medium text-gray-900 mb-2">
            Zamówienie:{' '}
            <span className="font-bold text-primary">{order?.orderNumber}</span>
          </h1>

          <div className="flex flex-wrap gap-3 mt-4">
            <div
              className={`px-3 py-1.5 rounded-full ${getOrderStatusColor(order.orderStatus)} flex items-center gap-1 text-sm font-medium`}
            >
              <Check fontSize="small" />
              Status zamówienia: {translateOrderStatus(order.orderStatus)}
            </div>

            <div
              className={`px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 flex items-center gap-1 text-sm font-medium ${getPaymentStatusColor(order.paymentStatus)}`}
            >
              <Receipt fontSize="small" />
              Status płatności: {translatePaymentStatus(order.paymentStatus)}
            </div>

            {order.orderStatus === OrderStatus.IN_PROGRESS &&
              order.deliveryMethod === DeliveryMethod.KURIER_INPOST &&
              order.packageNumber && (
                <div className="px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium flex items-center gap-1">
                  <LocalShipping fontSize="small" />
                  {translateDeliveryStatus(order.deliveryStatus)}
                </div>
              )}
          </div>

          {order.orderStatus === OrderStatus.IN_PROGRESS &&
            order.deliveryMethod === DeliveryMethod.KURIER_INPOST &&
            order.packageNumber && (
              <div className="mt-3 text-gray-600">
                Numer przesyłki:{' '}
                <span className="font-medium">{order.packageNumber}</span>
              </div>
            )}
        </motion.div>

        <Routes>
          <Route
            path="/"
            element={
              <motion.div variants={itemVariants}>
                {/* Główna sekcja zamówienia */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Lewa kolumna - informacje o dostawie i kontakt */}
                  <motion.div variants={itemVariants}>
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      {/* Informacje o płatności */}
                      {order.orderStatus !== OrderStatus.CANCELLED &&
                        order.paymentStatus !== PaymentStatus.COMPLETED &&
                        !isOnAdmin && (
                          <div className="bg-amber-50 border-b border-amber-200 p-4 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <Info
                                className="text-amber-600"
                                fontSize="small"
                              />
                              <p className="text-amber-800 text-sm font-medium">
                                Zamówienie oczekuje na płatność
                              </p>
                            </div>
                            <Link
                              to={`płatność`}
                              className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700 transition-colors"
                            >
                              Opłać teraz
                            </Link>
                          </div>
                        )}

                      {/* Sposób dostawy */}
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-medium text-gray-800 mb-3">
                          Sposób dostawy
                        </h3>
                        <div className="flex items-center">
                          <div className="p-2 bg-gray-50 rounded-md mr-3">
                            <img
                              src={getIconForDeliveryMethod(
                                order.deliveryMethod,
                              )}
                              alt={order.deliveryMethod}
                              className="h-8 w-auto"
                            />
                          </div>
                          <div>
                            <div className="font-medium">
                              {translateDeliveryMethod(order.deliveryMethod)}
                            </div>
                            {order.deliveryMethod ===
                              DeliveryMethod.PACZKOMAT && (
                              <div className="text-sm text-gray-600 mt-1">
                                Punkt odbioru:{' '}
                                <span className="font-medium">
                                  {order.paczkomat}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Adres dostawy (zaktualizowany) - dla wszystkich metod dostawy poza paczkomatem */}
                      {order.deliveryMethod !== DeliveryMethod.PACZKOMAT &&
                        (hasFullAddress(order) || hasAddressId(order)) && (
                          <div className="p-5 border-b border-gray-100">
                            <h3 className="text-base font-medium text-gray-800 mb-3">
                              Adres dostawy
                            </h3>

                            {/* Informacja o użyciu zapisanego adresu */}
                            {hasAddressId(order) && !hasFullAddress(order) && (
                              <div className="text-gray-600 mb-2">
                                <p>
                                  Używany jest zapisany adres (ID:{' '}
                                  {order.addressId})
                                </p>
                              </div>
                            )}

                            {/* Wyświetl pola adresowe gdy są dostępne */}
                            {hasFullAddress(order) && (
                              <div className="space-y-1 text-gray-600">
                                <p>
                                  <span className="text-gray-500">Ulica:</span>{' '}
                                  {order.street} {order.buildingNumber}
                                  {order.apartmentNumber &&
                                    `/${order.apartmentNumber}`}
                                </p>
                                <p>
                                  <span className="text-gray-500">Miasto:</span>{' '}
                                  {order.city}
                                </p>
                                <p>
                                  <span className="text-gray-500">
                                    Kod pocztowy:
                                  </span>{' '}
                                  {order.postalCode}
                                </p>
                                <p>
                                  <span className="text-gray-500">
                                    Województwo:
                                  </span>{' '}
                                  {order.province}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                      {/* Dane kontaktowe */}
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-medium text-gray-800 mb-3">
                          Dane kontaktowe
                        </h3>
                        <div className="space-y-1 text-gray-600">
                          {order.fullName && (
                            <p>
                              <span className="text-gray-500">
                                Imię i nazwisko:
                              </span>{' '}
                              {order.fullName}
                            </p>
                          )}
                          {order.email && (
                            <p>
                              <span className="text-gray-500">Email:</span>{' '}
                              {order.email}
                            </p>
                          )}
                          {order.phone && (
                            <p>
                              <span className="text-gray-500">Telefon:</span>{' '}
                              {order.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Dane do faktury */}
                      {order.NIP && (
                        <div className="p-5">
                          <h3 className="text-base font-medium text-gray-800 mb-3">
                            Dane do faktury
                          </h3>
                          <div className="space-y-1 text-gray-600">
                            <p>
                              <span className="text-gray-500">NIP:</span>{' '}
                              {order.NIP}
                            </p>
                            <p>
                              <span className="text-gray-500">
                                Nazwa firmy:
                              </span>{' '}
                              {order.companyName}
                            </p>
                            <p>
                              <span className="text-gray-500">Adres:</span>{' '}
                              {order.companyStreetAndNumber},{' '}
                              {order.companyPostalCode}, {order.companyCity}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Prawa kolumna - podsumowanie zamówienia */}
                  <motion.div variants={itemVariants}>
                    <div className="sticky top-8">
                      <h3 className="text-base font-medium text-gray-800 mb-3">
                        Zawartość zamówienia
                      </h3>
                      <PriceList order={order} showSummary={false} />

                      <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">
                            Wartość produktów:
                          </span>
                          <span className="font-medium">
                            {order.subTotalPrice} zł
                          </span>
                        </div>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-gray-600">Dostawa:</span>
                          <span className="font-medium">
                            {order.deliveryPrice} zł
                          </span>
                        </div>
                        <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                          <span className="font-medium text-gray-700">
                            Razem:
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {order.totalPrice} zł
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Sekcja pomocy */}
                <motion.div
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8"
                  variants={itemVariants}
                >
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h2 className="text-lg font-medium text-gray-800">
                      Wsparcie i pomoc
                    </h2>
                  </div>

                  <div className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-base font-medium text-gray-800 mb-3">
                          Zwrot lub anulowanie zamówienia
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Możesz skorzystać z formularza zwrotu online lub
                          skontaktować się z nami bezpośrednio. Forularz będzie
                          dostępny po zakończeniu zamówienia.
                        </p>
                      </div>

                      {order.orderStatus === OrderStatus.COMPLETED && (
                        <Link
                          to={`zwrot`}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 mt-3 md:mt-0"
                        >
                          <span>Formularz zwrotu</span>
                          <ArrowBack
                            sx={{ fontSize: 18, transform: 'rotate(180deg)' }}
                          />
                        </Link>
                      )}
                    </div>

                    <p className="text-gray-600 mb-4">
                      W celu anulowania zamówienia skontaktuj się z nami pod
                      adresem email:{' '}
                      <a
                        href={`mailto:${config.contact.email}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {config.contact.email}
                      </a>{' '}
                      lub telefonicznie:{' '}
                      <a
                        href="tel:+48604856094"
                        className="text-primary hover:underline font-medium"
                      >
                        +48 604 856 094
                      </a>
                      .
                    </p>
                    <p className="text-gray-600">
                      Zwrotu można dokonać w ciągu 14 dni od daty otrzymania
                      zamówienia.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            }
          />
          <Route
            path="/potwierdzenie"
            element={<OrderConfirmation orderId={id as string} />}
          />
          <Route
            path="/płatność"
            element={<PaymentForm onComplete={() => fetchOrder()} />}
          />
          <Route
            path="/zwrot"
            element={
              <motion.div variants={itemVariants}>
                <ReturnOrderForm
                  order={order}
                  onSubmit={handleReturnSubmit}
                  onCancel={() => navigate(`/zamówienie/${id}`)}
                />
              </motion.div>
            }
          />
        </Routes>

        {/* Modals */}
        <CancelOrder
          order={order}
          isModalVisible={showCancelOrderModal}
          setIsModalVisible={() => setShowCancelOrderModal(false)}
        />
        <Confirm
          message="Czy na pewno chcesz zmienić status zamówienia?"
          onConfirm={updateOrderStatus}
          isOpen={showConfirmChangeStatusModal}
          onClose={() => {
            setShowConfirmChangeStatusModal(false);
          }}
        />
        <Confirm
          message="Czy na pewno chcesz potwierdzić wysłanie przesyłki?"
          onConfirm={setPackageSent}
          isOpen={showPackageSentModal}
          onClose={() => {
            setShowPackageSentModal(false);
          }}
        />
        <Confirm
          message="Czy na pewno chcesz zamknąć zamówienie? Nie będzie można go już edytować."
          onConfirm={handleFinishOrder}
          isOpen={showFinishOrderModal}
          onClose={() => {
            setShowFinishOrderModal(false);
          }}
        />
      </motion.div>
      <Seo
        title={`Zamówienie ${order.orderNumber}`}
      ></Seo>
    </>
  );
}

export default Order;
